import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';

// Pages
import HomePage from './pages/HomePage';
import CarsPage from './pages/CarsPage';
import CarDetailPage from './pages/CarDetailPage';
import SellCarPage from './pages/SellCarPage';
import WikiPage from './pages/WikiPage';
import WikiBrandPage from './pages/WikiBrandPage';
import WikiModelPage from './pages/WikiModelPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import PublicProfilePage from './pages/PublicProfilePage';
import VINCheckPage from './pages/VINCheckPage';
import VINReportPage from './pages/VINReportPage';
import MagazinePage from './pages/MagazinePage';
import ArticlePage from './pages/ArticlePage';
import AdminPage from './pages/AdminPage';
import AuthCallback from './pages/AuthCallback';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CyberpunkBackground from './components/CyberpunkBackground';
import ChatWidget from './components/ChatWidget';

// Context & Hooks
import { LanguageProvider } from './LanguageContext';
import useAnalytics from './hooks/useAnalytics';
import { installMockInterceptor } from './services/mockApi';
import ErrorBoundary from './components/ErrorBoundary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Install mock interceptor for Netlify (static) deployment
if (process.env.REACT_APP_USE_MOCK === 'true' || (!BACKEND_URL && process.env.NODE_ENV === 'production')) {
  installMockInterceptor();
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Check for Google auth first
      const savedUser = localStorage.getItem('carhippo_user');
      const authMethod = localStorage.getItem('carhippo_auth_method');

      if (authMethod === 'google' && savedUser) {
        // Google users now also have a Bearer token (issued by /api/auth/google/session)
        // — use it instead of cross-origin cookies, which are blocked at the platform edge.
        const googleToken = localStorage.getItem('token');
        if (googleToken) {
          try {
            const response = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${googleToken}` }
            });
            setUser(response.data);
            setLoading(false);
            return;
          } catch {
            localStorage.removeItem('token');
            localStorage.removeItem('carhippo_user');
            localStorage.removeItem('carhippo_auth_method');
          }
        } else {
          // Older Google session (cookie-only). Try cookie auth once, then fall back.
          try {
            const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(response.data);
            setLoading(false);
            return;
          } catch {
            localStorage.removeItem('carhippo_user');
            localStorage.removeItem('carhippo_auth_method');
          }
        }
      }

      // Fall back to JWT auth
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', response.data.access_token);
    localStorage.removeItem('carhippo_auth_method');
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem('token', response.data.access_token);
    localStorage.removeItem('carhippo_auth_method');
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('carhippo_user');
    localStorage.removeItem('carhippo_auth_method');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const authMethod = localStorage.getItem('carhippo_auth_method');
    try {
      const config = authMethod === 'google'
        ? { withCredentials: true }
        : { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/auth/me`, config);
      setUser(response.data);
      if (authMethod === 'google') {
        localStorage.setItem('carhippo_user', JSON.stringify(response.data));
      }
      return response.data;
    } catch (e) {
      console.error('refreshUser failed:', e);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, setToken, login, register, logout, refreshUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020204]">
        <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020204]">
        <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user || !user.is_admin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Analytics Wrapper
const AnalyticsWrapper = ({ children }) => {
  useAnalytics();
  return children;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsWrapper>
            <AppShell />
          </AnalyticsWrapper>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

const AppShell = () => {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');

  return (
    <div className="min-h-screen bg-[#030810] text-white flex flex-col relative">
      {!isDashboard && <CyberpunkBackground />}
      {!isDashboard && <Navbar />}
      <main className={isDashboard ? '' : 'flex-1 relative z-10'}>
        <ErrorBoundary>
        <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/cars" element={<CarsPage />} />
                  <Route path="/cars/:id" element={<CarDetailPage />} />
                  <Route path="/sell" element={
                    <ProtectedRoute>
                      <SellCarPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/wiki" element={<WikiPage />} />
                  <Route path="/wiki/:brandName" element={<WikiBrandPage />} />
                  <Route path="/wiki/model/:modelId" element={<WikiModelPage />} />
                  <Route path="/vin-check" element={<VINCheckPage />} />
                  <Route path="/vin-report/:vin" element={<VINReportPage />} />
                  <Route path="/magazine" element={<MagazinePage />} />
                  <Route path="/magazine/:slug" element={<ArticlePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/*" element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/profile/:userId" element={<PublicProfilePage />} />
                  <Route path="/admin" element={
                    <AdminRoute>
                      <AdminPage />
                    </AdminRoute>
                  } />
                </Routes>
                </ErrorBoundary>
              </main>
              {!isDashboard && <Footer />}
              <ChatWidget />
              <Toaster 
                theme="dark" 
                position="top-right"
                toastOptions={{
                  style: {
                    background: '#0a0a0a',
                    border: '1px solid rgba(0, 255, 255, 0.2)',
                    color: '#fff',
                  },
                }}
              />
            </div>
  );
};

export default App;
